import { Badge, Button, Card, CardHeader, Drawer, Table, formatDate, useToast, type Column } from "@helmr/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { putProfile, type AbsenceBasis, type PayType, type ProfileEntry } from "../../api/payroll";
import { useServices } from "../../app/services";
import { useProfiles } from "./hooks";
import { Empty, Field, Gate, MoneyInput, css as s, errorText, isDate, parseMoneyInput, useScope } from "./kit";

/** Basis points from "12.5" (percent). String arithmetic, two decimals at most. */
export function parsePercentBp(text: string): number | null {
  const m = /^(\d+)(?:\.(\d{0,2}))?$/.exec(text.trim());
  if (!m) return null;
  const bp = Number(`${m[1]}${(m[2] ?? "").padEnd(2, "0")}`);
  return Number.isSafeInteger(bp) ? bp : null;
}
export const bpToPercent = (bp: number) => `${Math.trunc(bp / 100)}${bp % 100 ? `.${String(bp % 100).padStart(2, "0").replace(/0$/, "")}` : ""}`;
/** Paise → "1234.50" for editing, string maths. */
export function minorToInput(minor: number, digits = 2): string {
  const unit = 10 ** digits;
  const major = Math.trunc(Math.abs(minor) / unit);
  const rest = Math.abs(minor) % unit;
  return `${minor < 0 ? "-" : ""}${major}${rest ? `.${String(rest).padStart(digits, "0")}` : ""}`;
}

export function ProfilesTab() {
  const sc = useScope();
  const q = useProfiles(sc);
  const [editing, setEditing] = useState<ProfileEntry | null>(null);
  const canEdit = sc.ctx.has("payroll.profiles_edit");
  const cols: Array<Column<ProfileEntry>> = [
    { key: "name", header: "Staff", cell: (e) => <span className={s.truncate} title={e.name}>{e.name}</span> },
    { key: "type", header: "Type", cell: (e) => (e.profile ? (e.profile.payType === "salaried" ? "Salaried" : "Contractor") : <Badge tone="warn">No terms</Badge>) },
    { key: "base", header: "Monthly base", align: "end", cell: (e) => (e.profile ? <span className={s.num}>{sc.money(e.profile.baseMonthly)}</span> : "—") },
    { key: "joined", header: "Joined", cell: (e) => (e.profile ? formatDate(e.profile.joinedOn, sc.locale, "short") : "—") },
    ...(canEdit ? [{ key: "act", header: "Actions", srOnly: true, cell: (e: ProfileEntry) => <Button size="sm" onClick={() => setEditing(e)}>{e.profile ? "Edit" : "Add terms"}</Button> }] : []),
  ];
  return (
    <Card aria-label="Salary terms">
      <CardHeader title="Salary terms" subtitle="Per staff member. Changes apply from the next draft." />
      <Gate query={q} what="salary terms">
        {(d) => d.profiles.length === 0 ? <Empty icon="cash" title="No payroll profiles: add salary terms for your staff" description="Staff appear here once they are added under Staff." /> : (
          <Table label="Salary terms" columns={cols} rows={d.profiles} rowKey={(e) => e.staffId} minWidth={560} summary={`${d.profiles.length} staff`} />
        )}
      </Gate>
      {editing ? <ProfileDrawer entry={editing} onClose={() => setEditing(null)} /> : null}
    </Card>
  );
}

function ProfileDrawer({ entry, onClose }: { entry: ProfileEntry; onClose: () => void }) {
  const sc = useScope();
  const { client } = useServices();
  const qc = useQueryClient();
  const toast = useToast();
  const p = entry.profile;
  const [payType, setPayType] = useState<PayType>(p?.payType ?? "salaried");
  const [base, setBase] = useState(p ? minorToInput(p.baseMonthly, sc.digits) : "");
  const [tds, setTds] = useState(p ? (p.payType === "contractor" ? (p.tdsRateBp === null ? "" : bpToPercent(p.tdsRateBp)) : minorToInput(p.tdsMonthlyAmount, sc.digits)) : "");
  const [basis, setBasis] = useState<AbsenceBasis>(p?.absenceBasis ?? "days_in_month");
  const [joined, setJoined] = useState(p?.joinedOn ?? sc.today);
  const [left, setLeft] = useState(p?.leftOn ?? "");
  const [err, setErr] = useState<string | null>(null);

  const baseMinor = parseMoneyInput(base, sc.currency, sc.locale);
  const tdsValue = tds.trim() === "" ? 0 : payType === "contractor" ? parsePercentBp(tds) : parseMoneyInput(tds, sc.currency, sc.locale);
  const errs = {
    base: baseMinor === null ? "Enter an amount like 25000 or 25000.50." : null,
    tds: tdsValue === null ? (payType === "contractor" ? "Enter a percent like 10 or 2.5." : "Enter an amount.") : payType === "contractor" && tdsValue > 10_000 ? "At most 100%." : null,
    joined: isDate(joined) ? null : "Use a date like 2026-04-01.",
    left: left === "" || (isDate(left) && left >= joined) ? null : "Use a date on or after the joining date.",
  };
  const bad = Object.values(errs).some(Boolean);

  const save = useMutation({
    mutationFn: () => putProfile(client, entry.staffId, {
      payType, baseMonthly: baseMinor!, absenceBasis: basis, joinedOn: joined, leftOn: left === "" ? null : left,
      ...(payType === "contractor" ? { tdsRateBp: tdsValue === 0 && tds.trim() === "" ? null : tdsValue } : { tdsMonthlyAmount: tdsValue ?? 0 }),
    }),
    onSuccess: async () => { toast.show("Salary terms saved"); await qc.invalidateQueries({ queryKey: ["payroll"] }); onClose(); },
    onError: (e) => setErr(errorText(e)),
  });

  return (
    <Drawer open onOpenChange={(o) => !o && onClose()} title={`Salary terms · ${entry.name}`}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" disabled={bad} busy={save.isPending} onClick={() => save.mutate()}>Save terms</Button></>}>
      <div className={`${s.rows} ${s.drawerPad}`}>
        <Field label="Pay type">{(a) => <select {...a} className={s.input} value={payType} onChange={(e) => { setPayType(e.target.value as PayType); setTds(""); }}><option value="salaried">Salaried</option><option value="contractor">Contractor</option></select>}</Field>
        <Field label="Monthly base" error={base === "" ? null : errs.base}>{(a) => <MoneyInput {...a} value={base} onChange={setBase} />}</Field>
        <Field label={payType === "contractor" ? "TDS rate (%)" : "TDS per month"} error={errs.tds} hint={payType === "contractor" ? "Empty uses the rate in payroll settings" : "Optional"}>{(a) => <MoneyInput {...a} value={tds} onChange={setTds} />}</Field>
        <Field label="Absence counted over">{(a) => <select {...a} className={s.input} value={basis} onChange={(e) => setBasis(e.target.value as AbsenceBasis)}><option value="days_in_month">Days in month</option><option value="working_days">Working days</option></select>}</Field>
        <div className={s.grid2}>
          <Field label="Joined on" error={errs.joined}>{(a) => <input {...a} className={s.input} value={joined} onChange={(e) => setJoined(e.target.value)} placeholder="YYYY-MM-DD" />}</Field>
          <Field label="Left on" error={errs.left} hint="Optional">{(a) => <input {...a} className={s.input} value={left} onChange={(e) => setLeft(e.target.value)} placeholder="YYYY-MM-DD" />}</Field>
        </div>
        {err ? <p className={s.formErr} role="alert">{err}</p> : null}
      </div>
    </Drawer>
  );
}
